/**
 * Relative date ranges ("This Month", "YTD", …).
 *
 * A report run records the *preset* it was created with, not the dates that
 * preset happened to resolve to. Re-running it later re-resolves against the
 * current date, so a "This Month" report re-run in a later month reports that
 * month rather than staying pinned to the month it was first created in.
 */

export const RELATIVE_PRESETS = [
    "this_month",
    "last_month",
    "this_quarter",
    "last_quarter",
    "ytd",
] as const;

/** No date range at all (the Transactions screen's "All Dates"); resolves to null and stores no dates. */
export const ALL_DATES = "all_dates";

/**
 * Format a Date as YYYY-MM-DD in local time.
 *
 * `toISOString().slice(0, 10)` cannot be used here: the preset boundaries are
 * built with `new Date(y, m, d)`, which is local midnight, and converting that
 * to UTC moves the day backwards for any timezone east of UTC — turning
 * "1 Aug" into "31 Jul".
 */
export function toIsoDate(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Resolve a preset to concrete dates. Returns null for "custom" (and anything
 * unrecognised), meaning the caller's own absolute dates stand.
 */
export function resolveDatePreset(
    preset?: string | null,
    today: Date = new Date()
): { from: string; to: string } | null {
    let from: Date, to: Date;
    switch (preset) {
        case "this_month":
            from = new Date(today.getFullYear(), today.getMonth(), 1);
            to   = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            break;
        case "last_month":
            from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            to   = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
        case "this_quarter": {
            const q = Math.floor(today.getMonth() / 3);
            from = new Date(today.getFullYear(), q * 3, 1);
            to   = new Date(today.getFullYear(), q * 3 + 3, 0);
            break;
        }
        case "last_quarter": {
            let q = Math.floor(today.getMonth() / 3) - 1;
            let y = today.getFullYear();
            if (q < 0) { q = 3; y--; }
            from = new Date(y, q * 3, 1);
            to   = new Date(y, q * 3 + 3, 0);
            break;
        }
        case "ytd":
            from = new Date(today.getFullYear(), 0, 1);
            to   = today;
            break;
        // The Transactions screen's rolling windows, ending today as the web app's do.
        case "last_30":
            from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
            to   = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            break;
        case "last_90":
            from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 89);
            to   = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            break;
        default:
            return null;
    }
    return { from: toIsoDate(from), to: toIsoDate(to) };
}

/**
 * Recover the preset for a run recorded before the preset itself was stored.
 * Only works while those dates still line up with one of today's ranges; any
 * older run is reported as "custom" and keeps its absolute dates.
 */
export function inferPresetFromDates(from: string, to: string): string {
    for (const preset of RELATIVE_PRESETS) {
        const range = resolveDatePreset(preset);
        if (range && range.from === from && range.to === to) return preset;
    }
    return "custom";
}
